import { Ball, SimulationConfig, EscapeRing, ShapeType } from '@/types';
import { IRenderer } from './IRenderer';
import { WebGLRenderer } from './WebGLRenderer';
import { WebGPURenderer } from './WebGPURenderer';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';

export class GPURenderer implements IRenderer {
  private webglRenderer: WebGLRenderer;
  private webgpuRenderer: WebGPURenderer | null = null;
  private isWebGPUActive = false;
  private webgpuBlacklisted = false;
  private currentTier = 0;

  constructor(canvas: HTMLCanvasElement) {
    // 1. Initialise production-grade WebGLRenderer
    this.webglRenderer = new WebGLRenderer(canvas);
    this.isWebGPUActive = false;
    PerformanceTelemetry.webgpuTier = 0;
    
    // 2. Perform background robust WebGPU adapter negotiation
    this.tryInitializeWebGPU(canvas);
  }

  private calculateCapabilityTier(adapter: any): number {
    try {
      if (!adapter) return 0;
      const limits = adapter.limits || {};
      
      // Determine device description hints
      const info = adapter.info || {};
      const description = (info.description || '').toLowerCase();
      const vendor = (info.vendor || '').toLowerCase();
      
      // Blacklist known extremely low performance or software emulated adapters
      if (description.includes('swiftshader') || description.includes('microsoft basic render') || vendor.includes('swiftshader')) {
        console.info('GPURenderer: Software/Emulated WebGPU adapter detected. Assigning Tier 1 (Low-End).');
        return 1;
      }

      const maxBufferSize = limits.maxBufferSize || 0;
      const maxStorageBufferBindingSize = limits.maxStorageBufferBindingSize || 0;

      // Tier 3: Discrete heavy-duty workstation GPU profile (Nvidia RTX, Discrete AMD, Apple Max/Pro)
      if (maxBufferSize >= 512 * 1024 * 1024 && maxStorageBufferBindingSize >= 512 * 1024 * 1024) {
        console.info('GPURenderer: Dynamic high-performance WebGPU profile determined. Assigning Tier 3 (Discrete).');
        return 3;
      }

      // Tier 2: Standard Unified integrated memory profiles (Intel Iris, Apple standard M-series, mobile SoC)
      if (maxBufferSize >= 128 * 1024 * 1024) {
        console.info('GPURenderer: Mid-range unified/integrated WebGPU profile determined. Assigning Tier 2 (Unified).');
        return 2;
      }

      // Tier 1: Legacy or basic features
      console.info('GPURenderer: Basic entry-level WebGPU profile determined. Assigning Tier 1.');
      return 1;
    } catch (e) {
      console.warn('Error during WebGPU capability scoring:', e);
      return 1; // Default fallback to safe Tier 1
    }
  }

  private switchToWebGLFallback(reason: string, error?: any) {
    if (error) {
      console.error(`GPURenderer Fallback Recovery: ${reason}. Error details:`, error);
    } else {
      console.warn(`GPURenderer Fallback Recovery: ${reason}`);
    }

    this.isWebGPUActive = false;
    this.webgpuBlacklisted = true;
    PerformanceTelemetry.isWebGPUActive = false;
    PerformanceTelemetry.webgpuTier = 0;

    if (this.webgpuRenderer) {
      try {
        this.webgpuRenderer.dispose();
      } catch (disposeError) {
        console.warn('Failed to dispose crashed WebGPU context:', disposeError);
      }
      this.webgpuRenderer = null;
    }
  }

  private async tryInitializeWebGPU(canvas: HTMLCanvasElement) {
    if (this.webgpuBlacklisted) return;

    const gpu = (navigator as any).gpu;
    if (!gpu) {
      console.info('GPURenderer: WebGPU not natively supported. Using WebGL Engine.');
      return;
    }

    try {
      const adapter = await gpu.requestAdapter();
      if (!adapter) {
        console.warn('GPURenderer: WebGPU adapter requested but not discovered.');
        return;
      }

      // Calculate adapter capabilities
      this.currentTier = this.calculateCapabilityTier(adapter);
      PerformanceTelemetry.webgpuTier = this.currentTier;

      // Check device limits if needed
      console.info('GPURenderer: WebGPU Adapter found. Inspecting max buffer size:', adapter.limits.maxBufferSize);

      this.webgpuRenderer = new WebGPURenderer(canvas);
      
      // Handle context lost/device recovery bindings
      this.webgpuRenderer.onDeviceLost = () => {
        this.switchToWebGLFallback('Hot-swapping back to WebGL due to lost WebGPU context.');
      };

      // Poll/Check readiness asynchronously
      let attempts = 0;
      const verifyReady = () => {
        if (this.webgpuBlacklisted) return;

        if (this.webgpuRenderer && this.webgpuRenderer.isReady) {
          this.isWebGPUActive = true;
          PerformanceTelemetry.isWebGPUActive = true;
          PerformanceTelemetry.webgpuTier = this.currentTier;
          console.info(`GPURenderer Engine: WebGPU path activated successfully at Tier ${this.currentTier}!`);
        } else if (attempts < 15) {
          attempts++;
          setTimeout(verifyReady, 100);
        }
      };
      setTimeout(verifyReady, 100);
    } catch (e) {
      this.switchToWebGLFallback('WebGPU init crashed. Confirmed WebGL fallback safety.', e);
    }
  }

  getIsWebGPUActive(): boolean {
    return this.isWebGPUActive;
  }

  // General safer wrapper for dispatch with seamless fallback
  private selectRenderer<T>(callback: (renderer: IRenderer) => T): T {
    const active = this.isWebGPUActive && this.webgpuRenderer && this.webgpuRenderer.isReady && !this.webgpuBlacklisted;
    PerformanceTelemetry.isWebGPUActive = !!active;
    if (active) {
      try {
        PerformanceTelemetry.webgpuTier = this.currentTier;
        return callback(this.webgpuRenderer!);
      } catch (e) {
        this.switchToWebGLFallback('Critical WebGPU invocation crashed. Real-time fallback to WebGL triggered', e);
        return callback(this.webglRenderer);
      }
    }
    PerformanceTelemetry.webgpuTier = 0;
    return callback(this.webglRenderer);
  }

  resize(width: number, height: number): void {
    this.selectRenderer(r => r.resize(width, height));
  }

  clear(): void {
    this.selectRenderer(r => r.clear());
  }

  render(balls: Ball[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel?: number): void {
    this.selectRenderer(r => r.render(balls, config, camera, zoomLevel));
  }

  renderBallPreviews(previews: { pos: { x: number; y: number; }; vel: { x: number; y: number; }; radius: number; color: string; }[], config: SimulationConfig, camera: { x: number; y: number; scale: number; }, zoomLevel?: number): void {
    this.selectRenderer(r => r.renderBallPreviews(previews, config, camera, zoomLevel));
  }

  renderBlasts(blasts: any[], config: SimulationConfig, camera: { x: number; y: number; scale: number; }, zoomLevel?: number): void {
    this.selectRenderer(r => r.renderBlasts(blasts, config, camera, zoomLevel));
  }

  renderFX(events: any[], config: SimulationConfig, camera: { x: number; y: number; scale: number; }, zoomLevel?: number): void {
    this.selectRenderer(r => r.renderFX(events, config, camera, zoomLevel));
  }

  renderTrails(trailCircles: any[], config: SimulationConfig, camera: { x: number; y: number; scale: number; }, zoomLevel?: number): void {
    this.selectRenderer(r => r.renderTrails(trailCircles, config, camera, zoomLevel));
  }

  renderRings(rings: EscapeRing[], config: SimulationConfig, camera: { x: number; y: number; scale: number; }, morph: { from: ShapeType; to: ShapeType; factor: number; }, globalHue: number, zoomLevel?: number): void {
    this.selectRenderer(r => r.renderRings(rings, config, camera, morph, globalHue, zoomLevel));
  }

  renderContainer(config: SimulationConfig, camera: { x: number; y: number; scale: number; }, morph: { from: ShapeType; to: ShapeType; factor: number; }, radius: number, hue: number, zoomLevel?: number): void {
    this.selectRenderer(r => r.renderContainer(config, camera, morph, radius, hue, zoomLevel));
  }

  updateCollisionEvents(collisionEvents: any[], config: SimulationConfig): void {
    this.selectRenderer(r => r.updateCollisionEvents(collisionEvents, config));
  }

  renderGPUParticles(config: SimulationConfig, camera: { x: number; y: number; scale: number; }): void {
    this.selectRenderer(r => r.renderGPUParticles(config, camera));
  }

  prepareGPUPingPongTrails(config: SimulationConfig): void {
    this.selectRenderer(r => r.prepareGPUPingPongTrails(config));
  }

  renderBallsToTrails(balls: Ball[], config: SimulationConfig, camera: { x: number; y: number; scale: number; }, zoomLevel?: number): void {
    this.selectRenderer(r => r.renderBallsToTrails(balls, config, camera, zoomLevel));
  }

  drawGPUTrailsToScreen(): void {
    this.selectRenderer(r => r.drawGPUTrailsToScreen());
  }

  resetGPUPasses(): void {
    this.selectRenderer(r => r.resetGPUPasses());
  }

  dispose(): void {
    this.webglRenderer.dispose();
    if (this.webgpuRenderer) {
      try {
        this.webgpuRenderer.dispose();
      } catch (e) {
        console.warn('Error during WebGPURenderer disposal:', e);
      }
      this.webgpuRenderer = null;
    }
  }
}
